import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, AlertTriangle, Users, DollarSign, Eye, Clock, CheckCircle } from 'lucide-react';

const HowGAPSoldWithPCPPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                How GAP Insurance Was Sold With PCP — A Double Scandal
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                GAP insurance wasn't just mis-sold — it was often added on top of already mis-sold car finance
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                Here's what makes the GAP insurance scandal even worse: in many cases, it was added on top 
                of car finance agreements that were <strong>already mis-sold</strong>. Consumers were 
                being hit twice — first with hidden commission on their PCP or HP finance, then again 
                with hidden commission on the GAP policy sold alongside it.
              </p>
            </div>
          </div>
        </section>

        {/* Perfect Storm Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Perfect Storm at the Dealership
                </h2>
              </div>
              
              <div className="space-y-6 text-lg text-muted-foreground mb-8">
                <p>
                  Picture the scene: you've spent hours at the dealership. You've test driven the car, 
                  negotiated the price, and now you're in the finance office. You're tired. You're excited 
                  about your new car. <strong className="text-foreground">And that's exactly when they struck.</strong>
                </p>
                <p>
                  The finance manager presents GAP insurance as an essential add-on. They explain how your 
                  car will depreciate, how you could be left thousands of pounds out of pocket if it's 
                  written off. It sounds sensible.
                </p>
              </div>

              <Card className="border-destructive bg-destructive/5">
                <CardContent className="p-6">
                  <p className="text-lg font-semibold text-foreground">
                    What they don't tell you is that they're about to pocket 70% of whatever you pay.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Easy Win Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  GAP Insurance: The "Easy Win" for Dealers
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                For car dealers, GAP insurance was the perfect bolt-on sale:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Customers in "buying mode"</h3>
                    <p className="text-muted-foreground">The car deal was done, resistance was low. You were ready to sign anything to drive away.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Small sums, big profits</h3>
                    <p className="text-muted-foreground">What's another £300 compared to the car price? But £210 of that went straight to the dealer.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Fear factor worked</h3>
                    <p className="text-muted-foreground">No one wants to be left with debt on a written-off car. The pitch was designed to worry you.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Enormous commissions</h3>
                    <p className="text-muted-foreground">Up to 70% of the premium, with minimal effort. The incentive to sell was huge.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* What You Weren't Told Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <Eye className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  What You Weren't Told
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-8">
                The key to this scandal lies in what dealers <strong className="text-primary-foreground">failed to disclose</strong>:
              </p>

              <div className="space-y-6">
                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">1. GAP Insurance Was Optional</h3>
                  <p className="text-primary-foreground/80">
                    Many customers were given the impression that GAP insurance was required to get the 
                    finance, or that the deal wouldn't go through without it. <strong className="text-gold">This was false.</strong> 
                    GAP insurance was always optional.
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">2. You Could Buy It Elsewhere — For a Fraction of the Price</h3>
                  <p className="text-primary-foreground/80">
                    The exact same GAP cover that cost £300-£500 at the dealership could often be purchased 
                    online for <strong className="text-gold">under £100</strong>. Dealers had no incentive to tell you this — doing so would 
                    cost them hundreds of pounds in commission.
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">3. The Commission Structure</h3>
                  <p className="text-primary-foreground/80">
                    At no point were customers told that the dealer was pocketing 70% of the premium. 
                    If you'd known that <strong className="text-gold">only 30p of every pound</strong> was actually going towards your 
                    insurance cover, would you have bought it?
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">4. The 14-Day Cooling-Off Period</h3>
                  <p className="text-primary-foreground/80">
                    You had a legal right to cancel within 14 days for a full refund. 
                    <strong className="text-gold"> How many customers were clearly informed of this right?</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Double Dipping Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Added to Already Mis-Sold Finance
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                Here's where it gets even worse. We now know that <strong className="text-foreground">millions of PCP and HP finance 
                agreements were also mis-sold</strong> through Discretionary Commission Arrangements (DCAs). 
                Dealers could increase your interest rate to earn more commission — again, without telling you.
              </p>

              <Card className="border-destructive bg-destructive/5 mb-8">
                <CardContent className="p-6">
                  <p className="font-semibold text-foreground mb-4">So many consumers found themselves in this position:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-destructive text-xs">1</span>
                      </div>
                      <span className="text-muted-foreground">Paying inflated interest rates on their car finance (hidden commission)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-destructive text-xs">+</span>
                      </div>
                      <span className="text-muted-foreground"><strong className="text-foreground">Plus</strong> paying inflated premiums on GAP insurance (hidden commission)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-destructive text-xs">!</span>
                      </div>
                      <span className="text-muted-foreground">While being told neither</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <p className="text-lg text-muted-foreground">
                The finance industry had created a system where they profited at every turn, while 
                customers remained in the dark.
              </p>
            </div>
          </div>
        </section>

        {/* You May Have Two Claims */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  You May Have Two Claims
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                If you took out car finance with GAP insurance between 2007 and 2021, you may be entitled 
                to compensation on <strong className="text-foreground">both</strong>:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-accent bg-accent/5">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Your Car Finance Agreement</h3>
                    <p className="text-muted-foreground">If discretionary commission wasn't disclosed on your PCP or HP finance</p>
                  </CardContent>
                </Card>
                <Card className="border-gold bg-gold/5">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-2">Your GAP Insurance Policy</h3>
                    <p className="text-muted-foreground">If the commission and alternatives weren't disclosed when you bought it</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    <strong>Blue Lion Law can assess both claims together</strong>, ensuring you don't miss out on compensation 
                    you may be owed.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Check Both Your Claims
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Don't miss out on compensation. Blue Lion Law can assess your GAP insurance and car finance together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="/gap-insurance-claims">
                    Start Your Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="/pcp-car-finance-claims">
                    PCP Finance Claims
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/gap-insurance/what-is-gap-insurance" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">What Is GAP Insurance?</span>
                </a>
                <a href="/gap-insurance/fca-action-on-gap-insurance" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">FCA Action on GAP Insurance</span>
                </a>
                <a href="/pcp-car-finance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">PCP Car Finance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowGAPSoldWithPCPPage;
